import React, { useMemo } from 'react';
import { Pie } from '@ant-design/plots';
import { Col, Row, Typography } from 'antd';
import { useTheme } from '../../theme/ThemeProvider';
import {
  aggregateCountsByGroup,
  getRequestGroupLabel,
} from '../../utils/requestTypeLabels';

const { Text } = Typography;

const TOP_USERS = 6;

function withPercent(rows, labelField = 'label', valueField = 'value') {
  const total = rows.reduce((sum, r) => sum + r[valueField], 0);
  if (!total) return [];
  return rows.map((r) => ({
    ...r,
    percent: ((r[valueField] / total) * 100).toFixed(1),
    legendLabel: `${r[labelField]} (${((r[valueField] / total) * 100).toFixed(1)}%)`,
  }));
}

function buildCategoryPieData(series) {
  const map = aggregateCountsByGroup(
    (series || []).map((r) => ({ type: r.type, count: Number(r.count) || 0 })),
    'type',
    'count'
  );
  const rows = Object.entries(map).map(([key, count]) => ({
    label: getRequestGroupLabel(key),
    value: count,
  }));
  return withPercent(rows.sort((a, b) => b.value - a.value));
}

function buildUsersPieData(usersSummary) {
  const sorted = [...(usersSummary || [])]
    .filter((u) => u.requests_total > 0)
    .sort((a, b) => b.requests_total - a.requests_total);

  if (!sorted.length) return [];

  const top = sorted.slice(0, TOP_USERS);
  const rest = sorted.slice(TOP_USERS);
  const rows = top.map((u) => ({ label: u.username, value: u.requests_total }));

  if (rest.length) {
    rows.push({
      label: `Прочие (${rest.length})`,
      value: rest.reduce((sum, u) => sum + u.requests_total, 0),
    });
  }

  return withPercent(rows);
}

function buildTimePieData(overview) {
  const active = overview?.active_sec || 0;
  const passive = overview?.passive_sec || 0;
  if (active + passive <= 0) return [];
  return withPercent([
    { label: 'Активное (с запросами)', value: active },
    { label: 'Пассивное (ожидание)', value: passive },
  ]);
}

function DistributionPie({ title, hint, data, loading, height = 280 }) {
  const { darkMode } = useTheme();

  if (loading) {
    return (
      <div className="admin-pie-chart">
        <Text strong className="admin-pie-chart__title">{title}</Text>
        <div className="admin-pie-chart admin-pie-chart--loading">Загрузка…</div>
      </div>
    );
  }

  if (!data.length) {
    return (
      <div className="admin-pie-chart">
        <Text strong className="admin-pie-chart__title">{title}</Text>
        {hint && <Text type="secondary" className="admin-pie-chart__hint">{hint}</Text>}
        <Text type="secondary">Нет данных за период</Text>
      </div>
    );
  }

  return (
    <div className="admin-pie-chart">
      <Text strong className="admin-pie-chart__title">{title}</Text>
      {hint && <Text type="secondary" className="admin-pie-chart__hint">{hint}</Text>}
      <Pie
        data={data}
        angleField="value"
        colorField="label"
        radius={0.9}
        innerRadius={0.55}
        height={height}
        legend={{ position: 'bottom' }}
        theme={darkMode ? 'dark' : 'light'}
        label={{
          text: (d) => (Number(d.percent) >= 5 ? `${d.percent}%` : ''),
          style: { fontSize: 11, fontWeight: 500 },
        }}
        tooltip={{
          title: (d) => d.label,
          items: [{ field: 'value', name: 'Кол-во' }],
        }}
        statistic={{
          title: { content: 'Всего' },
          content: {
            content: String(data.reduce((s, r) => s + r.value, 0)),
          },
        }}
      />
    </div>
  );
}

const WorkDistributionCharts = ({
  series = [],
  usersSummary = [],
  overview = null,
  userId = null,
  loading = false,
}) => {
  const categoryData = useMemo(() => buildCategoryPieData(series), [series]);
  const usersData = useMemo(() => buildUsersPieData(usersSummary), [usersSummary]);
  const timeData = useMemo(() => buildTimePieData(overview), [overview]);

  const showUsersPie = !userId;
  const showTimePie = !!userId;

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} md={showUsersPie || showTimePie ? 12 : 24}>
        <DistributionPie
          title="Доля запросов по категориям"
          hint="ТЗ, заявки и WIPO объединены по вкладкам поиска."
          data={categoryData}
          loading={loading}
        />
      </Col>
      {showUsersPie && (
        <Col xs={24} md={12}>
          <DistributionPie
            title="Доля запросов по пользователям"
            hint={`Топ-${TOP_USERS} и сумма остальных.`}
            data={usersData}
            loading={loading}
          />
        </Col>
      )}
      {showTimePie && (
        <Col xs={24} md={12}>
          <DistributionPie
            title="Активное vs пассивное время"
            hint="Активное — интервалы с запросами в течение 5 мин после последнего."
            data={timeData}
            loading={loading}
          />
        </Col>
      )}
    </Row>
  );
};

export default WorkDistributionCharts;
